import {
  IconCopy,
  IconEdit,
  IconExternalLink,
  IconTrash,
} from "@tabler/icons-react";
import { EPDSearchOUT } from "api-client";
import clsx from "clsx";
import Tooltip from "components/Tooltip";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { ROUTES_ENUM } from "routes/consts";
import { epdSearchStore } from "../EPDSearchStore";
import ConfirmModal from "components/ConfirmModal";
import useToast from "hooks/useToast";
import { ProductActionMode } from "utils";

interface MenuItem {
  name: string;
  icon: JSX.Element;
  onClick: () => void;
  className: string;
  disabled: boolean;
}

const HoverMenu = ({ epd }: { epd: EPDSearchOUT }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openConfirmModal, setOpenConfirmModal] = React.useState(false);
  const toast = useToast();

  const onEditClicked = () => {
    navigate(`${ROUTES_ENUM.EPD_EDITOR}`, {
      state: {
        id: epd.id,
        mode: ProductActionMode.Edit,
      },
    });
  };

  const onDuplicateClicked = () => {
    navigate(`${ROUTES_ENUM.EPD_EDITOR}`, {
      state: {
        id: epd.id,
        mode: ProductActionMode.Duplicate,
      },
    });
  };

  const handleDeleteEPD = async () => {
    await epdSearchStore.deleteEPD(epd.id);
    const filteredItems = epdSearchStore.EPDs.items?.filter(
      (item) => item.id !== epd.id
    ) as EPDSearchOUT[];
    epdSearchStore.setEPDs({
      items: filteredItems,
      count: filteredItems.length,
    });
    toast(t("EPDEditor.deleteSuccess"), "success");
  };

  const buttons: MenuItem[] = [
    {
      name: t("EPDEditor.fileOfEPD"),
      icon: <IconExternalLink width={20} />,
      onClick: () => epd.url && window.open(epd.url, "_blank"),
      className: epd.url ? "block" : "!hidden",
      disabled: false,
    },
    {
      name: (epd as EPDSearchOUT).is_editable
        ? t("EPDEditor.editEPD")
        : t("EPDEditor.disabledEdit"),
      icon: <IconEdit width={20} />,
      onClick: () => onEditClicked(),
      className: "text-gray-700",
      disabled: !(epd as EPDSearchOUT).is_editable,
    },
    {
      name: t("EPDEditor.duplicateEPD"),
      icon: <IconCopy width={20} />,
      onClick: () => onDuplicateClicked(),
      className: "text-gray-700",
      disabled: false,
    },
    {
      name: t("EPDEditor.removeEPD"),
      icon: <IconTrash width={20} />,
      onClick: () => setOpenConfirmModal(true),
      className: "text-red-700 hover:text-red-500",
      disabled: false,
    },
  ];

  const onClickMenu = (e: React.MouseEvent, rec: MenuItem) => {
    e.stopPropagation();
    !rec.disabled && rec.onClick();
  };

  return (
    <div className="flex items-center ml-3">
      {buttons.map((rec, index) => (
        <Tooltip
          key={index}
          content={rec.name}
          hoverOnTooltip
          className={clsx(
            "flex items-center w-full py-2 text-indigo-700 mr-1.5 last-of-type:mr-0",
            rec.className
          )}
        >
          <div
            className={
              rec.disabled
                ? "opacity-50 cursor-not-allowed"
                : "opacity-100 !cursor-pointer"
            }
            onClick={(e) => onClickMenu(e, rec)}
          >
            {rec.icon}
          </div>
        </Tooltip>
      ))}
      <ConfirmModal
        open={openConfirmModal}
        close={() => setOpenConfirmModal(false)}
        type="warning"
        onConfirm={handleDeleteEPD}
        confirmButtonTitle={t("EPDEditor.deleteEPDButton")}
        title={t("EPDEditor.deleteEPDTitle", { epd_name: epd.name_de })}
        descriptionMesage={t("EPDEditor.deleteEPDDesc")}
        confirmButtonType="error"
      />
    </div>
  );
};

export default HoverMenu;
